import { PartyPopper } from "lucide-react";
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { api, ApiError, errorMessage } from "../lib/api";
import { toast } from "../lib/app";
import { EMOTES, showEmote, type Emote } from "./emotes";
import { Popover } from "./Popover";

interface Sent {
  id: string;
  readyAt: number;
  /** Everyone it reached, you included. */
  people: number;
}

/** The picker's grid is three across. */
const COLUMNS = 3;

function wait(ms: number): string {
  const minutes = Math.ceil(ms / 60_000);
  return minutes === 1 ? "a minute" : `${minutes} minutes`;
}

/** Restart a one-off CSS animation on every press. */
function replay(el: HTMLElement, className: string) {
  el.classList.remove("popped", "nope");
  void el.offsetWidth;
  el.classList.add(className);
}

/**
 * The party popper in the menu bar: pick confetti or another emote from a
 * grid and it plays on everyone's screen, once an hour per account until the
 * limit comes off.
 */
export function ConfettiButton() {
  /** When this account can send another emote. */
  const [readyAt, setReadyAt] = useState(0);
  /** When the hourly limit comes off for everyone. */
  const [unlimitedFrom, setUnlimitedFrom] = useState(Infinity);
  const [now, setNow] = useState(Date.now);
  /** The open picker's anchor. */
  const [picking, setPicking] = useState<HTMLElement | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const tiles = useRef<Array<HTMLButtonElement | null>>([]);
  /** Opened from the keyboard, so focus goes into the grid. */
  const byKeyboard = useRef(false);
  const left = readyAt - now;
  const unlimited = now >= unlimitedFrom;

  const refresh = () =>
    api
      .get<{ readyAt: number; unlimitedFrom: number }>("/api/confetti")
      .then((r) => {
        setReadyAt(r.readyAt);
        setUnlimitedFrom(r.unlimitedFrom);
        setNow(Date.now());
      })
      .catch(() => {});

  useEffect(() => {
    refresh();
  }, []);

  // Count the minutes down in the tooltip, light up again once the hour's over, and drop "one an hour" when the limit comes off.
  useEffect(() => {
    const next = left > 0 ? left : unlimitedFrom - now;
    if (next <= 0 || next === Infinity) return;
    const t = window.setTimeout(() => setNow(Date.now()), Math.min(next, 60_000));
    return () => window.clearTimeout(t);
  }, [left, unlimitedFrom, now]);

  useEffect(() => {
    if (picking && byKeyboard.current) tiles.current[0]?.focus();
  }, [picking]);

  const label =
    left > 0
      ? `Emotes for everyone: you can send another in ${wait(left)}`
      : unlimited
        ? "Send everyone an emote, as many as you like"
        : "Send everyone an emote (one an hour)";

  const send = (emote: Emote) => {
    setPicking(null);
    if (button.current) replay(button.current, "popped");
    api
      .post<Sent>("/api/confetti", { kind: emote.kind })
      .then((r) => {
        setReadyAt(r.readyAt);
        setNow(Date.now());
        showEmote(emote.kind, r.id, null);
        if (r.people <= 1) toast("Nobody else has a project open right now, so that one was just for you.");
      })
      .catch((err) => {
        // Sent from another tab or device this hour.
        if (err instanceof ApiError && err.status === 429) {
          toast(err.message);
          refresh();
        } else toast(errorMessage(err), "error");
      });
  };

  /** Arrow keys move around the grid. */
  const move = (e: KeyboardEvent, i: number) => {
    const step = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: COLUMNS, ArrowUp: -COLUMNS }[e.key];
    if (!step) return;
    e.preventDefault();
    tiles.current[(i + step + EMOTES.length) % EMOTES.length]?.focus();
  };

  return (
    <>
      <button
        ref={button}
        type="button"
        className={`confetti-btn${left > 0 ? " waiting" : ""}`}
        title={label}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={!!picking}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          const b = e.currentTarget;
          const ms = readyAt - Date.now();
          if (ms > 0) {
            replay(b, "nope");
            toast(`You've sent your emote for this hour. You can send another in ${wait(ms)}.`);
            return;
          }
          byKeyboard.current = e.detail === 0;
          setPicking(picking ? null : b);
        }}
      >
        <PartyPopper size={15} />
      </button>
      {picking && (
        <Popover anchor={picking} onClose={() => setPicking(null)} className="emote-pop" label="Send everyone an emote">
          <div className="emote-grid">
            {EMOTES.map((e, i) => (
              <button
                key={e.kind}
                ref={(el) => {
                  tiles.current[i] = el;
                }}
                type="button"
                className="emote-tile"
                onMouseDown={(ev) => ev.preventDefault()}
                onClick={() => send(e)}
                onKeyDown={(ev) => move(ev, i)}
              >
                <span className="emote-emoji" aria-hidden="true">
                  {e.emoji}
                </span>
                {e.name}
              </button>
            ))}
          </div>
          <div className="emote-foot">Plays for everyone with a project open</div>
        </Popover>
      )}
    </>
  );
}
